import { type CreateAccountInfoFormData, createAccountInfo } from '@musinsa/apis-curator';
import { lazyPipe } from '@musinsa/packages-functional';
import {
  validateServiceRequest,
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';

import type {
  CreateAccountInfoServiceRequest,
  CreateAccountInfoServiceResponse,
} from './createAccountInfo.dto';
import {
  CreateAccountInfoServiceRequestSchema,
  CreateAccountInfoServiceResponseSchema,
} from './createAccountInfo.dto';

// 파일 타입 가드 함수
const isFileOrBlob = (value: unknown): value is File | Blob =>
  value instanceof File || value instanceof Blob;

export const createAccountInfoService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(CreateAccountInfoServiceRequestSchema),

    async (request: CreateAccountInfoServiceRequest): Promise<CreateAccountInfoServiceResponse> => {
      const residentRegistrationNumber =
        request.businessType === 'NATURAL_PERSON' && request.idNumberFirst && request.idNumberSecond
          ? `${request.idNumberFirst}-${request.idNumberSecond}`
          : undefined;

      const formData: CreateAccountInfoFormData = {
        body: {
          businessCategory: request.businessType,
          bankName: request.bankName,
          bankAccountNumber: request.accountNumber,
          bankOwnerName: request.accountHolder,
          residentRegistrationNumber,
          registeredBusinessName: request.businessName,
          companyRegistrationNumber: request.businessRegistrationNumber,
        },
      };

      if (request.accountCopyFile && isFileOrBlob(request.accountCopyFile)) {
        formData.bankAccountImage = request.accountCopyFile;
      }

      if (request.identityFile && isFileOrBlob(request.identityFile)) {
        formData.idCardImage = request.identityFile;
      }

      if (request.businessLicenseFile && isFileOrBlob(request.businessLicenseFile)) {
        formData.businessRegistrationImage = request.businessLicenseFile;
      }

      // API 호출
      const apiResponse = await createAccountInfo(formData);

      // Service Layer DTO로 변환하여 반환
      const response: CreateAccountInfoServiceResponse = {
        businessCategory: apiResponse.data.businessCategory,
        bankName: apiResponse.data.bankName,
        bankAccountNumber: apiResponse.data.bankAccountNumber,
        bankOwnerName: apiResponse.data.bankOwnerName,
        residentRegistrationNumber: apiResponse.data.residentRegistrationNumber,
        registeredBusinessName: apiResponse.data.registeredBusinessName,
        companyRegistrationNumber: apiResponse.data.companyRegistrationNumber,
      };

      return response;
    },

    validateServiceResponse(CreateAccountInfoServiceResponseSchema),
  ),
);
